import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { get } from 'lodash';

import { WithPermissions, EDIT_PERMISSIONS } from 'containers/Permissions';
import Button from 'containers/QuestionnairePage/QuestionGroup/ABTest/Concepts/Button';
import conceptMessages from 'containers/QuestionnairePage/QuestionGroup/ABTest/Concepts/messages';
import groupMessages from 'containers/QuestionnairePage/QuestionGroup/messages';
import { isConjointGroup } from 'utils/nodeTypes';

import questionnaireMessages from 'containers/QuestionnairePage/messages';

import PfAlert from 'components/PfAlert';
import { TYPE_DANGER } from 'components/PfAlert/constants';

import { WithOptionalDrag } from 'components/DragAndDrop/Card';
import { DRAG_TYPE_GROUP_PROPERTY } from 'components/DragAndDrop/constants';

import {
  groupAddAttribute,
  conjointMoveAttribute,
} from 'containers/QuestionnairePage/actions';

import QuestionBlock from './QuestionBlock';
import Attribute from './Attribute';
import { getMaxAttributeValuesLimit, cannotAddAttribute } from './utils';
import {
  CONJOINT_ATTRIBUTE_TYPES,
  CONJOINT_MIN_ATTRIBUTES,
  MAXDIFF_MIN_ATTRIBUTES,
} from './constants';

function Conjoint(props) {
  const dispatch = useDispatch();
  const { group, onResolveComment, surveyId, hideActions } = props;
  const {
    questions,
    refId,
    properties,
    attributes,
    errors = {},
    pristineValues,
    type: groupType,
    isActive,
  } = group;
  const MIN_ATTRIBUTES = isConjointGroup(groupType)
    ? CONJOINT_MIN_ATTRIBUTES
    : MAXDIFF_MIN_ATTRIBUTES;
  const groupName = isConjointGroup(groupType) ? 'Conjoint' : 'Maxdiff';

  const [question] = questions;

  const onAddAttribute = useCallback(() => {
    dispatch(
      groupAddAttribute({
        refId,
        name: '',
        type: CONJOINT_ATTRIBUTE_TYPES.FEATURE,
        groupType,
      }),
    );
  }, [refId, groupType, dispatch]);

  const onMoveAttributeByDragging = useCallback(
    (payload) => {
      dispatch(
        conjointMoveAttribute({
          direction: payload.direction,
          refId: payload.groupRefId,
          attributeRefId: payload.refId,
        }),
      );
    },
    [dispatch],
  );

  const maxAttributeValues = useMemo(
    () => getMaxAttributeValuesLimit(attributes.length),
    [attributes.length],
  );

  const questionErrors = get(question, 'errors', {});
  return (
    <WithPermissions
      permission={[
        EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_ADD,
        EDIT_PERMISSIONS.CONJOINT_CONTENT_EDIT_PROMPT,
      ]}
      itemToCheck={question}
    >
      {(_, [hasAttributeAddPermission, hasContentEditPromptPermission]) => (
        <React.Fragment>
          <QuestionBlock
            question={question}
            groupRefId={refId}
            conceptsLabel={properties.conceptsLabel}
            canEditPrompt={hasContentEditPromptPermission}
            onResolveComment={onResolveComment}
            focus={isActive}
          />

          {questionErrors.emptyNoneOfTheAboveAnswer && (
            <PfAlert
              className="margin-top-xxs"
              noBackground
              noPadding
              title={
                <FormattedMessage
                  {...questionnaireMessages.errors.emptyNoneOfTheAboveAnswer}
                />
              }
              type={TYPE_DANGER}
            />
          )}

          {attributes.map((attribute, index) => (
            <WithPermissions
              key={attribute.refId}
              permission={EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_REORDER}
              itemToCheck={attribute}
            >
              {(hasPermission) => (
                <WithOptionalDrag
                  item={{
                    id: attribute.refId,
                    index,
                    type: DRAG_TYPE_GROUP_PROPERTY,
                    groupRefId: group.refId,
                    attributeIndex: index + 1,
                    acceptDrag: true,
                    acceptDrop: true,
                  }}
                  onDrop={onMoveAttributeByDragging}
                  disabled={!hasPermission}
                >
                  <Attribute
                    key={attribute.refId}
                    {...attribute}
                    index={index}
                    moveUpDisabled={index === 0}
                    moveDownDisabled={index === attributes.length - 1}
                    removeDisabled={attributes.length === MIN_ATTRIBUTES}
                    groupRefId={refId}
                    groupType={groupType}
                    pristineValues={pristineValues}
                    surveyId={surveyId}
                    focus={isActive}
                    maxAttributeValues={maxAttributeValues}
                    hideActions={hideActions}
                  />
                </WithOptionalDrag>
              )}
            </WithPermissions>
          ))}

          <div className="flex flex-items-center margin-top-md">
            {isConjointGroup(groupType) && (
              <Button
                onClick={onAddAttribute}
                message={conceptMessages.addAttribute}
                disabled={
                  !hasAttributeAddPermission ||
                  cannotAddAttribute(attributes.length)
                }
              />
            )}

            {errors.brandOrPriceTypeAttributesMoreThanOne && (
              <PfAlert
                noBackground
                noPadding
                title={
                  <FormattedMessage
                    {...groupMessages.errors
                      .brandOrPriceTypeAttributesMoreThanOne}
                  />
                }
                type={TYPE_DANGER}
                inlineStyle={false}
              />
            )}
            {errors.priceTypeAttributesMoreThanOne && (
              <PfAlert
                noBackground
                noPadding
                title={
                  <FormattedMessage
                    values={{ groupName }}
                    {...groupMessages.errors.priceTypeAttributesMoreThanOne}
                  />
                }
                type={TYPE_DANGER}
                inlineStyle={false}
              />
            )}
            {errors.brandTypeAttributesMoreThanOne && (
              <PfAlert
                noBackground
                noPadding
                title={
                  <FormattedMessage
                    {...groupMessages.errors.brandTypeAttributesMoreThanOne}
                  />
                }
                type={TYPE_DANGER}
                inlineStyle={false}
              />
            )}
          </div>
        </React.Fragment>
      )}
    </WithPermissions>
  );
}

Conjoint.propTypes = {
  group: PropTypes.object,
  surveyId: PropTypes.number,
};

export default Conjoint;
